import React, { useState } from 'react'
import { clsx } from 'clsx'
import { Area } from 'types/area'
import { OdinIcon } from '@/components/ui/odin/odin-icon'

type BaseRadio = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>

export type InputRadioExpandableProps = Omit<BaseRadio, 'type' | 'checked' | 'onChange'> & {
  value: string
  selectedValue: string | undefined
  onChange: (value: string) => void
  label?: string
  childrens?: Area[] // for recursive children
  level?: number
  disabled?: boolean
}

export const InputRadioExpandable = ({
  value,
  selectedValue,
  onChange,
  label,
  childrens,
  level = 0,
  disabled,
  ...rest
}: InputRadioExpandableProps) => {
  const [expanded, setExpanded] = useState(false)
  const hasChildren = childrens && childrens.length > 0
  const isSelected = selectedValue === value

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasChildren) {
      setExpanded(!expanded)
    }
  }

  const onChangeHandler = (value: string) => {
    onChange(value)
  }

  return (
    <div style={{ marginLeft: `${level * 8}px` }}>
      <div className={clsx('flex items-center text-sm mb-2 rounded-md px-1 py-1 hover:bg-accent transition-colors')}>
        <OdinIcon
          onClick={toggleExpand}
          className={clsx(
            'transition ml-0.5',
            expanded ? 'transform rotate-90' : '',
            !hasChildren && 'opacity-0',
            hasChildren && 'cursor-pointer',
          )}
          icon={['far', 'chevron-right']}
        />
        <div className={'flex items-center grow ml-2'}>
          <input
            type="radio"
            className="sr-only"
            checked={isSelected}
            onChange={() => !disabled && onChangeHandler(value)}
            {...rest}
          />
          <div
            className={clsx(
              'w-5 h-5 rounded-full border mr-2 shrink-0',
              'flex items-center justify-center',
              'transition-colors duration-200',
              isSelected ? 'bg-primary border-primary' : 'bg-background border-border',
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            )}
            onClick={() => !disabled && onChangeHandler(value)}
          >
            {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary-foreground" />}
          </div>
          {label && (
            <label
              className={clsx(
                'flex-1 truncate',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                isSelected ? 'font-semibold text-primary' : 'text-foreground',
              )}
              onClick={() => !disabled && onChangeHandler(value)}
            >
              {label}
            </label>
          )}
        </div>
      </div>
      {hasChildren && expanded && (
        <div className="ml-4 animate-in fade-in slide-in-from-top-1">
          {childrens.map((child) => (
            <InputRadioExpandable
              key={child._id}
              value={child._id}
              label={child.name}
              disabled={child.numberOfAreaSources === 0}
              childrens={child.children}
              selectedValue={selectedValue}
              onChange={onChangeHandler}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}
